import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Bus, MapPin, ShieldCheck, Smartphone, Users } from 'lucide-react';

const stats = [
  { value: '6+', label: 'Route hubs' },
  { value: '24/7', label: 'Digital booking' },
  { value: '100%', label: 'Ghana focused' },
];

const values = [
  {
    icon: ShieldCheck,
    title: 'Reliable operations',
    description: 'We design every trip around safer departures, clearer schedules, and dependable service standards.',
  },
  {
    icon: Smartphone,
    title: 'Simple booking',
    description: 'Customers can reserve seats, track trips, and manage bookings from one digital account.',
  },
  {
    icon: Users,
    title: 'Local service',
    description: 'Wote InterCity is built for Ghanaian intercity travelers, families, students, teams, and operators.',
  },
];

export function AboutUs() {
  return (
    <div className="min-h-screen bg-brand-white">
      <section className="relative overflow-hidden bg-brand-blue text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(245,150,30,0.24),transparent_32%),linear-gradient(135deg,rgba(9,58,124,1),rgba(6,32,70,1))]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-[0.92fr_1.08fr] gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-brand-orange font-bold uppercase tracking-wider mb-4">About Wote InterCity</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6">
              Building a clearer way to travel between Ghanaian cities.
            </h1>
            <p className="text-lg sm:text-xl text-blue-100 leading-relaxed max-w-2xl">
              Wote InterCity connects passengers to trusted routes, visible schedules, and account-based booking tools for easier intercity journeys.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/dashboard?tab=book"
                className="inline-flex items-center justify-center gap-2 bg-brand-orange text-white px-7 py-3 rounded-full font-bold hover:bg-brand-orange/90 transition-colors"
              >
                Book a Trip
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/routes"
                className="inline-flex items-center justify-center gap-2 border border-white/25 text-white px-7 py-3 rounded-full font-bold hover:bg-white/10 transition-colors"
              >
                View Routes
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative min-h-[22rem] rounded-lg overflow-hidden border border-white/15 bg-white/10"
          >
            <div className="absolute inset-0 bg-[linear-gradient(140deg,rgba(255,255,255,0.08),rgba(255,255,255,0.02))]" />
            <div className="absolute left-8 right-8 top-14 h-1 bg-white/20 rounded-full" />
            <div className="absolute left-[18%] top-12">
              <MapPin className="w-9 h-9 text-brand-orange drop-shadow" />
              <p className="mt-2 text-sm font-bold">Accra</p>
            </div>
            <div className="absolute left-[49%] top-12">
              <MapPin className="w-9 h-9 text-brand-orange drop-shadow" />
              <p className="mt-2 text-sm font-bold">Kumasi</p>
            </div>
            <div className="absolute right-[14%] top-12">
              <MapPin className="w-9 h-9 text-brand-orange drop-shadow" />
              <p className="mt-2 text-sm font-bold">Tamale</p>
            </div>
            <motion.div
              className="absolute top-[8.35rem] left-8"
              animate={{ x: [0, 180, 330, 0] }}
              transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
            >
              <div className="w-20 h-12 rounded-lg bg-brand-orange shadow-xl flex items-center justify-center">
                <Bus className="w-9 h-9 text-white" />
              </div>
            </motion.div>
            <div className="absolute bottom-0 inset-x-0 p-8 bg-gradient-to-t from-brand-blue via-brand-blue/80 to-transparent">
              <div className="grid grid-cols-3 gap-4">
                {stats.map((stat) => (
                  <div key={stat.label} className="border-l border-white/25 pl-4">
                    <p className="text-2xl font-extrabold">{stat.value}</p>
                    <p className="text-sm text-blue-100">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-blue mb-4">What we are building</h2>
            <p className="text-lg text-secondary-600 leading-relaxed">
              We are combining route management, passenger accounts, booking records, and operator tools into one transport platform for modern intercity service.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  className="border border-secondary-100 rounded-lg p-7 bg-brand-white"
                >
                  <div className="w-12 h-12 rounded-lg bg-brand-orange/10 flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-brand-orange" />
                  </div>
                  <h3 className="text-xl font-bold text-secondary-900 mb-3">{value.title}</h3>
                  <p className="text-secondary-600 leading-relaxed">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-secondary-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-blue mb-5">Travel should feel organized before the bus leaves.</h2>
          <p className="text-lg text-secondary-600 mb-8">
            Wote InterCity gives travelers the confidence to book, follow, and manage their trip without calls, uncertainty, or paper-only records.
          </p>
          <Link
            to="/careers"
            className="inline-flex items-center justify-center gap-2 text-brand-blue font-bold hover:text-brand-orange transition-colors"
          >
            Join the team
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
